import { usePackageBookingStore } from "@elixir/stores";
import { useEffect, useMemo, useState } from "react";
import { DropdownSelector } from "ui";
import useGetQueueConfig from "../../../../apis/tryst/get-queue-config";
import { useMyAccessProfileV2 } from "../../../../apis/tryst/get-my-access-profile";

const PackageSelection = () => {
  const setPackageDetails = usePackageBookingStore(
    (state) => state.setPackageDetails
  );
  const setSelectedClinicId = usePackageBookingStore(
    (state) => state.setSelectedClinicId
  );
  const showClinicSelector = usePackageBookingStore(
    (state) => state.showClinicSelector
  );

  const { data } = useGetQueueConfig();
  const { data: myAccessProfile } = useMyAccessProfileV2();

  const handleSetPackage = (package_id: string, package_name: string) => {
    const requiredPackage = data?.data?.packages.find(
      (pack) =>
        pack.package_id === package_id && pack.package_name === package_name
    );

    setPackageDetails(requiredPackage);
  };

  const [packageTitle, setPackageTitle] = useState<string>("");

  const packageListOptions = useMemo(() => {
    return (
      data?.data?.packages
        .filter((pack) => pack.active)
        .map((pack) => ({
          id: pack.package_id,
          name: pack.package_name,
        })) || []
    );
  }, [data?.data?.packages]);

  const [selectedClinicName, setSelectedClinicName] = useState<string>("");

  const clinicListOptions = useMemo(() => {
    return (
      myAccessProfile?.clinics?.map((clinic) => ({
        id: clinic.id,
        name: clinic.name,
      })) || []
    );
  }, [myAccessProfile?.clinics]);

  const doctorList = useMemo(() => {
    return (
      myAccessProfile?.doctors?.map((item) => ({
        id: item.oid,
        name: item.name,
      })) || []
    );
  }, [myAccessProfile?.doctors]);

  const conductingDoctor = usePackageBookingStore(
    (state) => state.conductingDoctor
  );
  const setConductingDoctor = usePackageBookingStore(
    (state) => state.setConductingDoctor
  );

  const showDoctorSelector = doctorList.length > 1;

  useEffect(() => {
    if (!conductingDoctor && myAccessProfile?.doctors?.length === 1) {
      //just one doctor. Autoselect. No need to show selector
      setConductingDoctor(myAccessProfile.doctors[0]);
    }
  }, [myAccessProfile?.doctors]);

  return (
    <div className="dr-flex dr-flex-row dr-gap-x-16">
      <div className="dr-flex dr-flex-col dr-gap-y-4">
        <div className="dr-flex dr-flex-row">
          <span className="BodyBold">Treatment package</span>
          <sup className="BodyBold dr-text-darwin-red-primary">*</sup>
        </div>
        <DropdownSelector
          bgColor="!dr-bg-darwin-neutral-0"
          customTitle={packageTitle || "Select a Package"}
          textColor={
            packageTitle
              ? "dr-text-darwin-neutral-1000"
              : "dr-text-darwin-neutral-600"
          }
          borderColor="dr-border-darwin-neutral-200"
          borderWidth="dr-border-1"
          options={packageListOptions}
          onSelect={({ id, name }: { id: string; name: string }) => {
            handleSetPackage(id, name);
            setPackageTitle(name);
          }}
        />
      </div>
      {showDoctorSelector ? (
        <div className="dr-flex dr-flex-col dr-gap-y-4">
          <div className="dr-flex dr-flex-row">
            <span className="BodyBold">Conducted by</span>
            <sup className="BodyBold dr-text-darwin-red-primary">*</sup>
          </div>
          <DropdownSelector
            bgColor="!dr-bg-darwin-neutral-0"
            customTitle={conductingDoctor?.name || "Select a Doctor"}
            textColor={
              conductingDoctor?.name
                ? "dr-text-darwin-neutral-1000"
                : "dr-text-darwin-neutral-600"
            }
            borderColor="dr-border-darwin-neutral-200"
            borderWidth="dr-border-1"
            options={doctorList}
            onSelect={({ id }: { id: string }) => {
              const requiredDoctor = myAccessProfile?.doctors?.find(
                (doctor) => doctor.oid === id
              );
              if (requiredDoctor) {
                setConductingDoctor(requiredDoctor);
              }
            }}
          />
        </div>
      ) : null}
      {showClinicSelector ? (
        <div className="dr-flex dr-flex-col dr-gap-y-4">
          <div className="dr-flex dr-flex-row">
            <span className="BodyBold">Clinics</span>
            <sup className="BodyBold dr-text-darwin-red-primary">*</sup>
          </div>
          <DropdownSelector
            bgColor="!dr-bg-darwin-neutral-0"
            customTitle={selectedClinicName || "Select a Clinic"}
            textColor={
              selectedClinicName
                ? "dr-text-darwin-neutral-1000"
                : "dr-text-darwin-neutral-600"
            }
            borderColor="dr-border-darwin-neutral-200"
            borderWidth="dr-border-1"
            options={clinicListOptions}
            onSelect={({ id, name }: { id: string; name: string }) => {
              setSelectedClinicId(id);
              setSelectedClinicName(name);
            }}
          />
        </div>
      ) : null}
    </div>
  );
};

export default PackageSelection;
